import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { Lock, X } from "lucide-react";
import { getAppConfig } from "../services/apiService";

/**
 * Subscription / entitlement gating for post-login UI.
 *
 * Fetches /auth/app-config once and exposes which in-app modules are locked
 * (not in the subscription plan). Components use `isModuleLocked(id)` to hide or
 * lock entry points, and `requestLocked(label)` to pop the shared "outside your
 * subscription" modal. The backend independently 403s the SCH/PRM routers, so
 * this is presentation only.
 */
interface SubscriptionValue {
  isModuleLocked: (moduleId: string) => boolean;
  lockMessage: string;
  requestLocked: (moduleLabel: string) => void;
}

const DEFAULT_MESSAGE =
  "This Module is not in the Subscription Plan - contact your admin to take this Subscription.";

// Safe defaults so a component rendered outside the provider never crashes.
const SubscriptionContext = createContext<SubscriptionValue>({
  isModuleLocked: () => false,
  lockMessage: DEFAULT_MESSAGE,
  requestLocked: () => {},
});

export const useSubscription = () => useContext(SubscriptionContext);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [lockedModules, setLockedModules] = useState<string[]>([]);
  const [lockMessage, setLockMessage] = useState<string>(DEFAULT_MESSAGE);
  const [modal, setModal] = useState<{ open: boolean; label: string }>({
    open: false,
    label: "",
  });

  useEffect(() => {
    let alive = true;
    getAppConfig()
      .then((cfg) => {
        if (!alive) return;
        setLockedModules(cfg.locked_modules || []);
        if (cfg.lock_message) setLockMessage(cfg.lock_message);
      })
      .catch(() => {
        /* default = nothing locked */
      });
    return () => {
      alive = false;
    };
  }, []);

  const isModuleLocked = useCallback(
    (moduleId: string) => lockedModules.includes(moduleId),
    [lockedModules],
  );

  const requestLocked = useCallback(
    (moduleLabel: string) => setModal({ open: true, label: moduleLabel }),
    [],
  );

  const closeModal = useCallback(() => setModal((m) => ({ ...m, open: false })), []);

  return (
    <SubscriptionContext.Provider
      value={{ isModuleLocked, lockMessage, requestLocked }}
    >
      {children}
      {modal.open && (
        <LockedModuleModal
          label={modal.label}
          message={lockMessage}
          onClose={closeModal}
        />
      )}
    </SubscriptionContext.Provider>
  );
};

/**
 * Route guard — renders a full-page "outside your subscription" notice instead
 * of the module when it's locked (covers direct-URL navigation). The backend
 * also 403s the module's API, so this is the UI half of defense-in-depth.
 */
export const ModuleGuard: React.FC<{
  moduleId: string;
  label: string;
  children: React.ReactNode;
}> = ({ moduleId, label, children }) => {
  const { isModuleLocked, lockMessage } = useSubscription();
  if (!isModuleLocked(moduleId)) return <>{children}</>;
  return (
    <div
      data-testid="PAGE_module_locked"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
        minHeight: "60vh",
        textAlign: "center",
        padding: 24,
      }}
    >
      <div
        style={{
          width: 88,
          height: 88,
          borderRadius: 22,
          background: "rgba(124,58,237,0.10)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#7c3aed",
        }}
      >
        <Lock size={40} />
      </div>
      <h2 style={{ margin: 0, color: "#0f172a" }}>{label}</h2>
      <p style={{ margin: 0, maxWidth: 460, color: "#475569", lineHeight: 1.5 }}>
        {lockMessage}
      </p>
    </div>
  );
};

/* Self-contained modal (inline styles) so it needs no global CSS and matches the
   login subscription modal's look. */
const LockedModuleModal: React.FC<{
  label: string;
  message: string;
  onClose: () => void;
}> = ({ label, message, onClose }) => (
  <div
    onClick={onClose}
    data-testid="MOD_module_locked_overlay"
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(15, 23, 42, 0.55)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 99999,
      padding: 16,
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "relative",
        background: "#fff",
        borderRadius: 20,
        padding: "40px 36px 28px",
        width: "100%",
        maxWidth: 420,
        textAlign: "center",
        boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
      }}
    >
      <button
        onClick={onClose}
        aria-label="Close"
        data-testid="BTN_module_locked_close"
        style={{
          position: "absolute",
          top: 16,
          right: 16,
          border: "none",
          background: "transparent",
          cursor: "pointer",
          color: "#64748b",
        }}
      >
        <X size={20} />
      </button>
      <div
        style={{
          width: 88,
          height: 88,
          margin: "0 auto 18px",
          borderRadius: 22,
          background: "rgba(124,58,237,0.10)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#7c3aed",
        }}
      >
        <Lock size={40} />
      </div>
      <h3 style={{ margin: "0 0 12px", fontSize: 22, color: "#0f172a" }}>
        {label}
      </h3>
      <p style={{ margin: "0 0 24px", color: "#475569", lineHeight: 1.5 }}>
        {message}
      </p>
      <button
        onClick={onClose}
        data-testid="BTN_module_locked_got_it"
        style={{
          border: "none",
          cursor: "pointer",
          background: "#7c3aed",
          color: "#fff",
          fontWeight: 600,
          padding: "12px 36px",
          borderRadius: 999,
          fontSize: 15,
        }}
      >
        Got It
      </button>
    </div>
  </div>
);
